'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, Briefcase, Building } from 'lucide-react';

export default function OnboardingPage() {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50/60 dark:from-emerald-950/30 dark:to-teal-950/20 flex flex-col relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-emerald-300/10 to-teal-300/20 dark:from-emerald-700/10 dark:to-teal-700/20 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-emerald-200/20 to-teal-200/10 dark:from-emerald-800/10 dark:to-teal-800/20 rounded-full blur-3xl transform -translate-x-1/4 translate-y-1/4"></div>
        <div className="hidden md:block absolute top-1/4 left-1/3 w-72 h-72 border border-emerald-200/20 dark:border-emerald-700/20 rounded-full"></div>
        <div className="hidden md:block absolute bottom-1/4 right-1/3 w-48 h-48 border border-teal-200/20 dark:border-teal-700/20 rounded-full"></div>
      </div>

      <div className="container mx-auto px-4 py-16 flex-1 flex flex-col justify-center max-w-5xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-block mb-6">
            <motion.div 
              className="h-20 w-20 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
            Welcome to <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-teal-500 dark:from-emerald-400 dark:to-teal-300">Huntier</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Choose how you'd like to use our platform to accelerate your career or hiring journey
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto w-full">
          {/* Job Seeker Card */}
          <Link href="job-seeker" className="block group">
            <Card 
              className="h-full cursor-pointer transition-all overflow-hidden border border-emerald-100 dark:border-emerald-900/50 shadow-lg hover:shadow-xl relative rounded-2xl group-hover:border-emerald-300 dark:group-hover:border-emerald-700"
              onMouseEnter={() => setHoveredCard('job-seeker')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <motion.div 
                className="p-8 flex flex-col h-full relative bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm"
                animate={{
                  backgroundColor: hoveredCard === 'job-seeker' ? 'rgba(16, 185, 129, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Decorative corner element */}
                <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden pointer-events-none opacity-30 group-hover:opacity-70 transition-opacity">
                  <div className="absolute top-0 right-0 -mt-12 -mr-12 w-24 h-24 bg-gradient-to-br from-emerald-500 to-emerald-300 dark:from-emerald-600 dark:to-emerald-400 rotate-12 transform origin-bottom-left"></div>
                </div>

                <div className="mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-400 dark:from-emerald-600 dark:to-teal-500 flex items-center justify-center shadow-md group-hover:shadow-lg transition-all transform group-hover:scale-110">
                    <Briefcase className="h-8 w-8 text-white" />
                  </div>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  I'm looking for a job
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                  Upload your resume, set your preferences, and let our AI match you with the perfect opportunities.
                </p>

                <ul className="mb-8 space-y-2 text-sm">
                  <motion.li 
                    className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="h-5 w-5 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>AI-powered job matching based on your skills</span>
                  </motion.li>
                  <motion.li 
                    className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <div className="h-5 w-5 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Personalized career recommendations</span>
                  </motion.li>
                </ul>
                
                <div className="mt-auto flex justify-between items-center">
                  <Button 
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-md hover:shadow-lg transition-all px-6 py-5 rounded-xl group-hover:scale-[1.02] text-base"
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  
                  <motion.div
                    animate={{
                      opacity: hoveredCard === 'job-seeker' ? 1 : 0,
                      x: hoveredCard === 'job-seeker' ? 0 : 10,
                    }}
                    transition={{ duration: 0.2 }}
                    className="text-emerald-500 dark:text-emerald-400"
                  >
                    <ArrowRight className="h-5 w-5" />
                  </motion.div>
                </div>
              </motion.div>
            </Card>
          </Link>

          {/* Recruiter Card */}
          <Link href="recruiter" className="block group">
            <Card 
              className="h-full cursor-pointer transition-all overflow-hidden border border-teal-100 dark:border-teal-900/50 shadow-lg hover:shadow-xl relative rounded-2xl group-hover:border-teal-300 dark:group-hover:border-teal-700"
              onMouseEnter={() => setHoveredCard('recruiter')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <motion.div 
                className="p-8 flex flex-col h-full relative bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm"
                animate={{
                  backgroundColor: hoveredCard === 'recruiter' ? 'rgba(20, 184, 166, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Decorative corner element */}
                <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden pointer-events-none opacity-30 group-hover:opacity-70 transition-opacity">
                  <div className="absolute top-0 right-0 -mt-12 -mr-12 w-24 h-24 bg-gradient-to-br from-teal-500 to-teal-300 dark:from-teal-600 dark:to-teal-400 rotate-12 transform origin-bottom-left"></div>
                </div>
                
                <div className="mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-400 dark:from-teal-600 dark:to-emerald-500 flex items-center justify-center shadow-md group-hover:shadow-lg transition-all transform group-hover:scale-110">
                    <Building className="h-8 w-8 text-white" />
                  </div>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  I'm hiring talent
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                  Create job postings, set candidate criteria, and let our AI find the perfect match for your team.
                </p>
                
                <ul className="mb-8 space-y-2 text-sm">
                  <motion.li 
                    className="flex items-center gap-2 text-teal-700 dark:text-teal-400"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="h-5 w-5 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center flex-shrink-0">
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>AI-powered candidate matching</span>
                  </motion.li>
                  <motion.li 
                    className="flex items-center gap-2 text-teal-700 dark:text-teal-400"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <div className="h-5 w-5 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center flex-shrink-0">
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Smart talent acquisition tools</span>
                  </motion.li>
                </ul>

                <div className="mt-auto flex justify-between items-center">
                  <Button 
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white shadow-md hover:shadow-lg transition-all px-6 py-5 rounded-xl group-hover:scale-[1.02] text-base"
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  
                  <motion.div
                    animate={{
                      opacity: hoveredCard === 'recruiter' ? 1 : 0,
                      x: hoveredCard === 'recruiter' ? 0 : 10,
                    }}
                    transition={{ duration: 0.2 }}
                    className="text-teal-500 dark:text-teal-400"
                  >
                    <ArrowRight className="h-5 w-5" />
                  </motion.div>
                </div>
              </motion.div>
            </Card>
          </Link>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/70 dark:bg-slate-800/70 rounded-full shadow-sm backdrop-blur-sm">
            <div className="flex -space-x-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500/80 to-teal-500/80 flex items-center justify-center text-[10px] text-white border-2 border-white dark:border-slate-800">
                  {i}
                </div>
              ))}
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              <span className="font-medium">1,000+</span> professionals joined today
            </span>
          </div>
          
          <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
            Already have an account? <Link href="../login" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">Log in</Link>
          </div>
        </motion.div>
      </div>
      
      <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/5 dark:from-emerald-900/20 dark:to-teal-900/10 w-full py-3">
        <div className="container mx-auto px-4 text-center text-xs text-emerald-800/70 dark:text-emerald-200/70">
          By continuing, you agree to Huntier's <Link href="../terms" className="underline hover:text-emerald-600 dark:hover:text-emerald-400">Terms of Service</Link> and <Link href="../privacy" className="underline hover:text-emerald-600 dark:hover:text-emerald-400">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}
